/**
 * Helper utilities for offer management
 * These functions help manage offers state and operations using the centralized config
 */

import { OFFERS_CONFIG, getOfferConfigById } from './offersConfig';

/**
 * Updates offer categories based on available offers
 * Adds/removes categories dynamically based on what offers are available
 * 
 * @param {Object} currentCategories - Current offer categories state
 * @param {Object} offersData - Object containing all offer arrays/objects
 * @returns {Object} Updated categories
 */
export const updateOfferCategories = (currentCategories, offersData) => {
    const updatedCategories = { ...currentCategories };

    // Helper: does a given stateKey hold any offers?
    const stateKeyHasOffers = (stateKey) => {
        const data = offersData[stateKey];
        return Array.isArray(data)
            ? data.length > 0
            : Object.keys(data || {}).length > 0;
    };

    Object.values(OFFERS_CONFIG).forEach(config => {
        if (config.excludeFromFilters || config.id === null) {
            return; // Skip non-filterable categories (incl. group sub-offers)
        }

        // A group category (e.g. Special Events) is visible if ANY of its
        // member sub-offers has data.
        const hasOffers = (config.isGroup && Array.isArray(config.subKeys))
            ? config.subKeys.some(subKey => {
                const subConfig = OFFERS_CONFIG[subKey];
                return subConfig ? stateKeyHasOffers(subConfig.stateKey) : false;
            })
            : stateKeyHasOffers(config.stateKey);

        if (!hasOffers) {
            // Remove category if no offers
            delete updatedCategories[config.key];
        } else {
            // Add or keep category if offers exist
            if (!updatedCategories[config.key]) {
                updatedCategories[config.key] = {
                    type: config.id,
                    isVisible: config.isVisible,
                    isNew: config.isNew,
                };
            }
        }
    });

    return updatedCategories;
};

/**
 * Selects offers from state based on offer type
 * 
 * @param {number} offerType - The offer type ID
 * @param {Object} offersState - Object containing all offer state arrays/objects
 * @returns {Array} Array of offers or empty array
 */
export const getOffersByType = (offerType, offersState) => {
    const config = getOfferConfigById(offerType);
    if (!config) return [];

    const offers = offersState[config.stateKey];
    
    // Handle different data structures
    if (Array.isArray(offers)) {
        return offers;
    } else if (typeof offers === 'object' && offers !== null) {
        // For resources, commanders, etc that are objects
        // Flatten the object structure into an array
        return Object.values(offers).flat();
    }
    
    return [];
};

/**
 * Get all offers as a flat array for searching
 * 
 * @param {Object} offersState - Object containing all offer state
 * @returns {Array} Flat array of all offers
 */
export const getAllOffersArray = (offersState) => {
    const allOffers = [];

    Object.values(OFFERS_CONFIG).forEach(config => {
        if (config.stateKey && offersState[config.stateKey]) {
            const offers = offersState[config.stateKey];
            
            if (Array.isArray(offers)) {
                allOffers.push(...offers);
            } else if (typeof offers === 'object' && offers !== null) {
                // Handle object-based offers (resources, commanders)
                Object.values(offers).forEach(offerGroup => {
                    if (Array.isArray(offerGroup)) {
                        allOffers.push(...offerGroup);
                    } else if (offerGroup) {
                        allOffers.push(offerGroup);
                    }
                });
            }
        }
    });

    return allOffers;
};

/**
 * Get configuration name for filter localStorage
 * 
 * @param {number} offerType - The offer type ID
 * @returns {string} Configuration key name
 */
export const getConfigNameByType = (offerType) => {
    const config = getOfferConfigById(offerType);
    return config ? config.key : null;
};

/**
 * Initialize offers state from API response
 * Uses the centralized config to map API keys to state keys
 * 
 * @param {Object} apiResponse - Raw API response
 * @param {Function} formatEventPassOffers - Function to format event pass offers
 * @returns {Object} Initialized offers state
 */
export const initializeOffersFromApi = (apiResponse, formatEventPassOffers = null) => {
    const offersState = {};

    Object.values(OFFERS_CONFIG).forEach(config => {
        // Skip group/pseudo configs that own no state (e.g. Special Events)
        if (!config.stateKey) {
            return;
        }

        if (config.apiResponseKey && apiResponse[config.apiResponseKey] !== undefined) {
            let data = apiResponse[config.apiResponseKey];

            // Special handling for event pass offers
            if (config.requiresFormatting && formatEventPassOffers) {
                data = formatEventPassOffers(data);
            }

            offersState[config.stateKey] = data || (Array.isArray(data) ? [] : {});
        } else {
            // Initialize with empty array or object
            offersState[config.stateKey] = config.renderType === 'custom' ? {} : [];
        }
    });

    return offersState;
};

/**
 * Check if at least one category would remain visible
 * Used to prevent hiding all categories
 * 
 * @param {Object} categories - Current categories state
 * @param {number} typeToToggle - Type ID that would be toggled
 * @returns {boolean} True if at least one category would remain visible
 */
export const wouldHaveVisibleCategory = (categories, typeToToggle) => {
    const activeCategories = Object.values(categories).filter(
        category => category.isVisible && category.type !== typeToToggle
    );
    return activeCategories.length > 0;
};
